import { useState, useEffect } from 'react';
import { API_BASE_URL } from '../lib/constants';
import styles from './ProductSelectionModal.module.css';

interface Product {
  name: string;
  price: number;
  url: string;
  imageUrl: string;
  customerRating: number;
  numReviews: number;
}

interface ProductSelectionModalProps {
  isOpen: boolean;
  onClose: () => void;
  recipeId: number;
  ingredientIndex: number;
  ingredientText: string;
  defaultSearchTerm: string;
  onProductSelected: (updatedIngredient: any) => void;
  existingProductData?: any; // For showing currently selected product
}

export default function ProductSelectionModal({
  isOpen,
  onClose,
  recipeId,
  ingredientIndex,
  ingredientText,
  defaultSearchTerm,
  onProductSelected,
  existingProductData
}: ProductSelectionModalProps) {
  const [searchTerm, setSearchTerm] = useState(defaultSearchTerm);
  const [products, setProducts] = useState<Product[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [selecting, setSelecting] = useState(false);
  const [selectedProductIndex, setSelectedProductIndex] = useState<number | null>(null);
  const [usagePercentage, setUsagePercentage] = useState(
    existingProductData?.usage_percentage || 25
  );

  const token = localStorage.getItem('token');

  useEffect(() => {
    if (isOpen && defaultSearchTerm) {
      setSearchTerm(defaultSearchTerm);
      searchProducts(defaultSearchTerm);
    }
  }, [isOpen, defaultSearchTerm]);

  const searchProducts = async (query: string) => {
    if (!query.trim()) return;
    
    setLoading(true);
    setError('');
    
    console.log('Searching products...', {
      url: `${API_BASE_URL}/api/recipes/${recipeId}/search-products/`,
      query,
      token: token ? 'present' : 'missing'
    });
    
    try {
      const response = await fetch(`${API_BASE_URL}/api/recipes/${recipeId}/search-products/`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          search_term: query,
          limit: 10
        })
      });

      console.log('Search response status:', response.status);

      if (response.ok) {
        const data = await response.json();
        console.log('Search response data:', data);
        
        let searchProducts = data.products || [];
        
        // If there's existing product data, add it at the top
        if (existingProductData && existingProductData.product_name) {
          const existingProduct: Product = {
            name: existingProductData.product_name,
            price: existingProductData.product_price || 0,
            url: existingProductData.product_url || '',
            imageUrl: existingProductData.product_image_url || '',
            customerRating: 0,
            numReviews: 0
          };
          
          // Check if this product is already in search results to avoid duplicates
          const isDuplicate = searchProducts.some((p: Product) => 
            p.name === existingProduct.name && p.price === existingProduct.price
          );
          
          if (!isDuplicate) {
            searchProducts = [existingProduct, ...searchProducts];
            // Auto-select the existing product
            setSelectedProductIndex(0);
          } else {
            // Find and select the existing product in search results
            const existingIndex = searchProducts.findIndex((p: Product) => 
              p.name === existingProduct.name && p.price === existingProduct.price
            );
            if (existingIndex >= 0) {
              setSelectedProductIndex(existingIndex);
            }
          }
        }
        
        setProducts(searchProducts);
      } else {
        const responseText = await response.text();
        console.error('Search error response:', response.status, responseText);
        setError(`Failed to search products: ${response.status}`);
      }
    } catch (err) {
      setError(`Error searching products: ${err instanceof Error ? err.message : String(err)}`);
      console.error('Error searching products:', err);
    } finally {
      setLoading(false);
    }
  };

  const selectProduct = (productIndex: number) => {
    setSelectedProductIndex(productIndex);
    setError('');
  };

  const confirmSelection = async () => {
    if (selectedProductIndex === null) return;
    
    setSelecting(true);
    setError('');

    const selectedProduct = products[selectedProductIndex];

    try {
      const response = await fetch(`${API_BASE_URL}/api/recipes/${recipeId}/select-product/`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          ingredient_index: ingredientIndex,
          product: {
            name: selectedProduct.name,
            price: selectedProduct.price,
            url: selectedProduct.url,
            imageUrl: selectedProduct.imageUrl
          },
          usage_percentage: usagePercentage,
          search_term: searchTerm  // Pass the search term used
        })
      });

      console.log('Response status:', response.status);
      console.log('Response headers:', response.headers);
      
      if (response.ok) {
        const data = await response.json();
        console.log('Success response data:', data);
        onProductSelected(data.updated_ingredient);
        onClose();
      } else {
        const responseText = await response.text();
        console.error('Error response:', response.status, responseText);
        try {
          const errorData = JSON.parse(responseText);
          setError(errorData.message || 'Failed to select product');
        } catch {
          setError(`HTTP ${response.status}: ${responseText || 'Failed to select product'}`);
        }
      }
    } catch (err) {
      setError(`Error selecting product: ${err instanceof Error ? err.message : String(err)}`);
      console.error('Error selecting product:', err);
    } finally {
      setSelecting(false);
    }
  };

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    searchProducts(searchTerm);
  };

  if (!isOpen) return null;

  return (
    <div className={styles.overlay} onClick={onClose}>
      <div className={styles.modal} onClick={(e) => e.stopPropagation()}>
        <div className={styles.header}>
          <h2 className={styles.title}>Select Product for "{ingredientText}"</h2>
          <button onClick={onClose} className={styles.closeButton}>×</button>
        </div>

        <form onSubmit={handleSearch} className={styles.searchForm}>
          <div className={styles.searchContainer}>
            <input
              id="product-search-input"
              type="text"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="Search for products..."
              className={styles.searchInput}
            />
            <button type="submit" disabled={loading} className={styles.searchButton}>
              {loading ? 'Searching...' : 'Search'}
            </button>
          </div>
        </form>

        {error && (
          <div className={styles.error}>{error}</div>
        )}

        <div className={styles.productsContainer}>
          {loading ? (
            <div className={styles.loading}>Searching for products...</div>
          ) : products.length === 0 ? (
            <div className={styles.noProducts}>No products found. Try a different search term.</div>
          ) : (
            <div className={styles.productsGrid}>
              {products.map((product, index) => {
                // Check if this is the currently selected product (when editing existing)
                const isCurrentlySelected = existingProductData && 
                  product.name === existingProductData.product_name && 
                  product.price === existingProductData.product_price;
                
                return (
                <div key={index} className={`${styles.productCard} ${isCurrentlySelected ? styles.currentProduct : ''}`}>
                  {isCurrentlySelected && (
                    <div className={styles.currentProductBadge}>
                      Currently Selected
                    </div>
                  )}
                  <div className={styles.productImageContainer}>
                    {product.imageUrl ? (
                      <img
                        src={product.imageUrl}
                        alt={product.name}
                        className={styles.productImage}
                        onError={(e) => {
                          e.currentTarget.style.display = 'none';
                          const nextElement = e.currentTarget.nextElementSibling as HTMLElement;
                          if (nextElement) {
                            nextElement.style.display = 'flex';
                          }
                        }}
                      />
                    ) : null}
                    <div className={styles.productImagePlaceholder} style={{
                      display: product.imageUrl ? 'none' : 'flex'
                    }}>
                      🛒
                    </div>
                  </div>

                  <div className={styles.productInfo}>
                    <h4 className={styles.productName}>{product.name}</h4>
                    <div className={styles.productPrice}>${product.price}</div>
                    
                    {product.customerRating > 0 && (
                      <div className={styles.productRating}>
                        <span className={styles.ratingStar}>⭐</span>
                        <span>{product.customerRating}</span>
                        {product.numReviews > 0 && (
                          <span>({product.numReviews} reviews)</span>
                        )}
                      </div>
                    )}

                    <div className={styles.productActions}>
                      <button
                        onClick={() => selectProduct(index)}
                        className={`${styles.selectButton} ${selectedProductIndex === index ? styles.selectedButton : ''}`}
                      >
                        {selectedProductIndex === index ? '✓ Selected' : 'Select This Product'}
                      </button>
                      <a
                        href={product.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className={styles.viewButton}
                      >
                        View on Walmart
                      </a>
                    </div>
                  </div>
                </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Usage percentage and action buttons */}
        <div className={styles.modalFooter}>
          <div className={styles.usageSection}>
            <label className={styles.usageLabel}>
              Usage Amount:
            </label>
            <div className={styles.usageInput}>
              <input
                type="number"
                min="1"
                max="100"
                value={usagePercentage}
                onChange={(e) => setUsagePercentage(Math.max(1, Math.min(100, parseInt(e.target.value) || 25)))}
                className={styles.percentageInput}
              />
              <span className={styles.percentSymbol}>%</span>
            </div>
            <div className={styles.usageHelp}>
              What portion of the selected product will you use?
            </div>
          </div>

          <div className={styles.modalActions}>
            <button
              onClick={onClose}
              className={styles.cancelButton}
            >
              Cancel
            </button>
            <button
              onClick={confirmSelection}
              disabled={selectedProductIndex === null || selecting}
              className={styles.doneButton}
            >
              {selecting ? 'Processing...' : 'Done'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}